import React from 'react';
import i18n from '@src/configs/i18n';
import ImportRejectedRows from '@components/SharedComponents/ImportExport/ImportRejectedRows';
import {
  loadingToSuccess,
  loadingToError,
  loadingToWarning,
  showSuccessToast,
  showErrorToast,
  showWarningToast
} from '@components/Notify/Toast';
import { createAsyncOperationTracker, refreshAffectedList } from '../createAsyncOperationTracker';

export const importTracker = createAsyncOperationTracker({
  prefix: 'excel-import',
  loadingMessage: 'Importing data... You will be notified once it completes',
  pendingTtl: 900000, // long imports can outlive the toast; keep polling for 15 minutes
  completedTtl: 180000,
  toastDuration: 120000,
  getPayload: (response) => response?.data?.data ?? response?.data ?? response ?? null,
  getOperationId: (payload) => payload?.operation_id || payload?.job_id
});

// Backward-compatible named exports (mirror the original app-side module).
export const getImportPayload = importTracker.getPayload;
export const isQueuedImportResponse = importTracker.isQueuedResponse;
export const getImportToastId = importTracker.getToastId;
export const handleQueuedImportResponse = importTracker.handleQueuedResponse;
export const completeImportOperation = importTracker.completeOperation;

const getMessage = (data, fallbackMessages) => {
  if (data?.status === 'failed') {
    if (typeof data?.error === 'string' && data.error.trim()) return data.error;
    if (Array.isArray(data?.messages)) return data.messages.join(' ');
    return 'Import operation failed.';
  }
  if (data?.status === 'blocked') {
    return `Full import is not allowed: ${data?.blockers_count ?? 0} record(s) are linked to other records.`;
  }
  if (data?.imported_count != null || data?.rejected_count != null) {
    return i18n.t('Import completed summary', {
      imported: data?.imported_count ?? 0,
      rejected: data?.rejected_count ?? 0
    });
  }
  const messages = data?.messages ?? fallbackMessages;
  if (Array.isArray(messages)) return messages.join(' ');
  if (typeof messages === 'string' && messages.trim()) return messages;
  return 'Imported';
};

const hasRejectedRows = (data) => Array.isArray(data?.rejected_rows) && data.rejected_rows.length > 0;

const resultContent = (data, summary) =>
  hasRejectedRows(data) ? React.createElement(ImportRejectedRows, { data, summary }) : summary;

// The rejected rows list needs a longer and wider toast than a plain summary
const resultToastOptions = (data) =>
  hasRejectedRows(data) ? { duration: 20000, style: { maxWidth: '480px' } } : {};

export const showImportResult = (response) => {
  const data = getImportPayload(response);
  showSuccessToast(resultContent(data, getMessage(data, response?.data?.messages)), resultToastOptions(data));
};

const handleImportStatus = (data, { dispatch } = {}) => {
  const operationId = data?.operation_id || data?.job_id;
  const context = completeImportOperation(operationId, data);
  const toastId = operationId ? getImportToastId(operationId) : null;
  const message = getMessage(data);
  const status = data?.status;

  if (status === 'failed') {
    if (toastId) loadingToError(toastId, message);
    else showErrorToast(message);
  } else if (status === 'blocked') {
    if (toastId) loadingToWarning(toastId, message);
    else showWarningToast(message);
  } else {
    const content = resultContent(data, message);
    if (toastId) loadingToSuccess(toastId, content, resultToastOptions(data));
    else showSuccessToast(content, resultToastOptions(data));
  }

  // blocked = nothing was imported, the list is unchanged.
  if (status !== 'blocked') {
    refreshAffectedList(context);
    context?.onComplete?.(data, { dispatch });
  }
};

export const importHandlers = {
  'excel-import.status': handleImportStatus,
  'import.status': handleImportStatus,
  'excel_import.status': handleImportStatus
};
